"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
} from "@hugeicons/core-free-icons";
import type { CarouselArrowsProps } from "./types";

/**
 * Carousel navigation arrows component
 * Supports RTL by default - arrows are swapped for RTL direction
 */
export const CarouselArrows = React.forwardRef<
  HTMLDivElement,
  CarouselArrowsProps
>(
  (
    {
      showPrev = true,
      showNext = true,
      className,
      prevClassName,
      nextClassName,
      canScrollPrev = true,
      canScrollNext = true,
      onPrevClick,
      onNextClick,
      variant = "default",
    },
    ref
  ) => {
    const baseStyles = {
      default:
        "!bg-white items-center justify-center border border-gray-100 rounded-sm p-0 size-[34px]",
      minimal:
        "h-auto hover:bg-transparent text-white disabled:invisible disabled:pointer-events-none",
      overlay:
        "absolute top-1/2 -translate-y-1/2 z-10 size-10 bg-white rounded-sm flex items-center justify-center shadow-[-4px_4px_4px_0px_#00000040] transition-all text-primary-500 cursor-pointer",
    };

    return (
      <div ref={ref} className={cn("flex items-center gap-4", className)}>
        {showPrev && (
          <Button
            variant={variant === "minimal" ? "ghost" : "outline"}
            size="icon"
            className={cn(
              baseStyles[variant],
              "-scale-x-100", // RTL: flip the prev arrow
              prevClassName
            )}
            disabled={!canScrollPrev}
            onClick={onPrevClick}
            aria-label="Previous slide"
          >
            {variant === "minimal" ? (
              <HugeiconsIcon icon={ArrowRight01Icon} className="size-14" />
            ) : (
              <HugeiconsIcon icon={ArrowLeft01Icon} className="size-4" />
            )}
          </Button>
        )}
        {showNext && (
          <Button
            variant={variant === "minimal" ? "ghost" : "outline"}
            size="icon"
            className={cn(baseStyles[variant], nextClassName)}
            disabled={!canScrollNext}
            onClick={onNextClick}
            aria-label="Next slide"
          >
            {variant === "minimal" ? (
              <HugeiconsIcon icon={ArrowLeft01Icon} className="size-14" />
            ) : (
              <HugeiconsIcon icon={ArrowLeft01Icon} className="size-4" />
            )}
          </Button>
        )}
      </div>
    );
  }
);

CarouselArrows.displayName = "CarouselArrows";

/**
 * Overlay navigation arrows (positioned absolutely on left/right)
 */
export const CarouselOverlayArrows = React.forwardRef<
  HTMLDivElement,
  CarouselArrowsProps & { show?: boolean }
>(({ show = true, canScrollPrev, canScrollNext, onPrevClick, onNextClick, className }, ref) => {
  if (!show) return null;

  return (
    <div ref={ref} className={className}>
      <button
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 size-10 bg-white rounded-sm flex items-center justify-center shadow-[-4px_4px_4px_0px_#00000040] transition-all text-primary-500 cursor-pointer disabled:opacity-0"
        onClick={onNextClick}
        disabled={!canScrollNext}
        aria-label="Next slide"
      >
        <HugeiconsIcon icon={ArrowLeft01Icon} strokeWidth={2.5} />
      </button>

      <button
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 size-10 bg-white rounded-sm flex items-center justify-center shadow-[-4px_4px_4px_0px_#00000040] transition-all text-primary-500 cursor-pointer disabled:opacity-0"
        onClick={onPrevClick}
        disabled={!canScrollPrev}
        aria-label="Previous slide"
      >
        <HugeiconsIcon icon={ArrowRight01Icon} strokeWidth={2.5} />
      </button>
    </div>
  );
});

CarouselOverlayArrows.displayName = "CarouselOverlayArrows";

